"use client";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";

import { PasswordResetUser } from "@/types/passwordReset";
import { RecaptchaResponse } from "@/types/recaptcha";
import {
  QuestionRecord,
  SecuritySettingsContextType,
} from "@/types/securitySettings";
import { UserEmail, UserIndicator } from "@/types/user";

import { getErrorMessage } from "@/lib/data";
import { httpRequest } from "@/lib/http";
import {
  fetchAnswerValidity,
  fetchSecurityQuestions,
} from "@/lib/securitySettings";

import { useRecaptcha } from "@/context/util/recaptcha";
import { useUi } from "@/context/util/ui";

import { getGeneralErrorAlert } from "@/components/alerts/global";
import CountdownTimer from "@/components/countdown";
import passwordResetAlerts from "@/components/alerts/passwordReset";

const SecuritySettingsContext = createContext<SecuritySettingsContextType>(
  null!
);

export function SecuritySettingsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { handleSetAlert, loading, setLoading, setSnackbar } = useUi();
  const { recaptchaRequest } = useRecaptcha();

  const [questions, setQuestions] = useState<QuestionRecord[]>([]);

  const timer = useRef<ReactNode | null>(null);
  const timerSpan = useRef<HTMLSpanElement | null>(null);

  const getSecurityQuestions = useCallback(() => {
    const request = async (init?: RequestInit) => {
      try {
        const questions = await fetchSecurityQuestions(init);

        if ("valid" in questions) return questions;

        setQuestions(questions);

        if (loading) setLoading(false);
      } catch (error) {
        const message =
          "Failed to get security questions. Please try again later.";
        console.error(message, error);

        setLoading(false);
        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));
      }
    };

    return recaptchaRequest("questions_lookup", request);
  }, [recaptchaRequest]);

  const isAnswerValid = useCallback(
    (
      username: string,
      securityQuestion: QuestionRecord,
      userIndicator: UserIndicator,
      source: string = "Account Utilities"
    ) => {
      const request = async (init?: RequestInit) => {
        window.scrollTo(0, 0);
        setLoading(true);

        try {
          const body = JSON.stringify({ securityQuestion, source, username });

          const answerValidityResponse = await fetchAnswerValidity(
            init?.headers ?? {},
            body
          );

          if ("valid" in answerValidityResponse) return answerValidityResponse;

          return answerValidityResponse;
        } catch (error) {
          const message = "Failed to validate your security answer";
          console.error(message, error);

          handleSetAlert(
            [1],
            getGeneralErrorAlert(message, error, userIndicator)
          );
        } finally {
          setLoading(false);
        }
      };
      return recaptchaRequest("answer_lookup", request);
    },
    [recaptchaRequest]
  );

  const sendQuestionResetEmail = useCallback(
    (
      email: UserEmail,
      user: PasswordResetUser,
      set: Dispatch<SetStateAction<PasswordResetUser | null>>
    ) => {
      const request = async (init?: RequestInit) => {
        setLoading(true);

        try {
          const url = `/account-utilities/api/password-reset/email`;

          const body = JSON.stringify({
            id: email.id,
            username: user.username,
          });
          const headers = { ...(init?.headers ?? {}) };
          const method = "POST";

          const requestInit: RequestInit = { body, headers, method };

          const sendEmailResponse = (
            await httpRequest<
              | (Pick<PasswordResetUser, "token" | "tokenExpiration"> & {
                  message: string;
                })
              | RecaptchaResponse
            >(url, requestInit)
          ).data;

          if (!sendEmailResponse) throw new Error("Empty http response");

          if ("valid" in sendEmailResponse) {
            handleSetAlert([1], passwordResetAlerts.getRecaptchaAlert());
            return sendEmailResponse;
          }

          const { token, tokenExpiration } = sendEmailResponse;

          set(
            (prev) =>
              ({
                ...(prev ?? {}),
                email,
                token,
                tokenExpiration,
              } as PasswordResetUser)
          );

          timer.current = (
            <CountdownTimer<PasswordResetUser>
              fields={["token"]}
              ref={timerSpan}
              setExpired={set}
              targetDate={sendEmailResponse.tokenExpiration}
            />
          );

          handleSetAlert(
            [1],
            passwordResetAlerts.getSendEmailSuccessAlert(
              email.address,
              user?.userIndicator ?? "EM",
              timer.current
            )
          );
        } catch (error) {
          const message = "Failed to send reset email. Please try again later.";
          console.error(message, error);

          handleSetAlert(
            [1],
            passwordResetAlerts.getGeneralErrorAlert(
              message,
              getErrorMessage(error),
              user?.userIndicator ?? "EM"
            )
          );
        } finally {
          setLoading(false);
        }
      };

      return recaptchaRequest("send_reset_email", request);
    },
    [recaptchaRequest]
  );

  return (
    <SecuritySettingsContext.Provider
      value={{
        getSecurityQuestions,
        isAnswerValid,
        questions,
        sendQuestionResetEmail,
        timer,
        timerSpan,
      }}
    >
      <Box mt={2} sx={{ width: "100%" }}>
        {children}
      </Box>
    </SecuritySettingsContext.Provider>
  );
}

export function useSecuritySettings() {
  const ctx = useContext(SecuritySettingsContext);
  if (!ctx)
    throw new Error(
      "useSecuritySettings must be used within a SecuritySettingsContext"
    );
  return ctx;
}
